import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@alertwhale/ui'
import { useNotifications } from '@/hooks/useNotifications'
import { useApplications } from '@/hooks/useApplications'
import { Bell, Send, Loader2, RefreshCw, Filter, Search } from 'lucide-react'
import { formatDate, cn } from '@/lib/utils'
import { SendNotificationDialog } from '@/components/notifications/SendNotificationDialog'

export function NotificationsPage() {
  const { data, isLoading, refetch } = useNotifications()
  const { data: applications } = useApplications()
  const [sendDialogOpen, setSendDialogOpen] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)

  const notifications = data || []
  const hasApps = applications && applications.length > 0

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refetch()
    setIsRefreshing(false)
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'bg-destructive/10 text-destructive border-destructive/20'
      case 'low':
        return 'bg-muted text-muted-foreground border-muted'
      default:
        return 'bg-primary/10 text-primary border-primary/20'
    }
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-gradient">Notifications</span>
            </h1>
            <p className="text-muted-foreground mt-1">View and send push notifications</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              variant="glow"
              onClick={() => setSendDialogOpen(true)}
              disabled={!hasApps}
            >
              <Send className="h-4 w-4" />
              Send Notification
            </Button>
          </div>
        </div>

        {/* No Apps Warning */}
        {!hasApps && (
          <div className="glass-card rounded-xl p-5 border-warning/30 bg-warning/5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/20 text-warning">
                <Bell className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-warning">Create an Application First</h3>
                <p className="text-sm text-muted-foreground">
                  You need to create an application before you can send notifications.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Loading notifications...</span>
            </div>
          </div>
        ) : notifications.length === 0 ? (
          <Card className="glass-card border-0">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Bell className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No notifications sent yet</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Send your first notification to see it here. Notifications help you engage with your users in real-time.
              </p>
              <Button
                variant="glow"
                onClick={() => setSendDialogOpen(true)}
                disabled={!hasApps}
              >
                <Send className="h-4 w-4" />
                Send Your First Notification
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="glass-card border-0">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg">
                Recent Notifications
                <span className="ml-2 text-sm font-normal text-muted-foreground">
                  ({notifications.length} total)
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className="flex items-start gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-primary/20 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Bell className="w-6 h-6 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="font-semibold group-hover:text-primary transition-colors">
                          {notification.title}
                        </p>
                        <Badge
                          variant="outline"
                          className="text-xs bg-white/[0.03]"
                        >
                          {notification.event_type}
                        </Badge>
                      </div>
                      {notification.body && (
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                          {notification.body}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatDate(notification.created_at)}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn('text-xs capitalize', getPriorityColor(notification.priority))}
                    >
                      {notification.priority}
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Send Notification Dialog */}
      <SendNotificationDialog
        open={sendDialogOpen}
        onOpenChange={setSendDialogOpen}
      />
    </>
  )
}
